/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import _ from 'lodash';
import { getFormValues } from 'redux-form';

import { RenderSelect2 } from 'src/components/views/common/render-select-2-v2';

import { loadCSS, loadScript, loadScriptWithCallback, removeScript } from 'src/helpers';
import {
  convertToShopifyThemeSetting,
  convertToDataThemeSetting,
  checkNewVersionBoostThemeLib
} from 'src/helpers/theme-settings-helper';
import BundlePreviewVisualEditor from './preview-bundle';
import { TextStyle } from '@shopify/polaris';
import { routes } from 'src/routes';
import { URL } from 'src/constants/links';
import { REACT_DOM_SCRIPT, REACT_SCRIPT } from 'src/constants/integration';
import { buildRecommendationTaeFeatures } from 'src/helpers/recommendation-helper';
import { getShop } from 'src/helpers';

const page = {
  'home-page': 'index',
  'product-page': 'product',
  'collection-page': 'collection',
  'cart-page': 'cart'
};
class PreviewVisualEditor extends Component<ComposedProps, State> {
  mainScript = null;
  mainCSS = null;
  vendorScript = null;
  filterScript = null;
  reactScript = null;
  reactDomScript = null;
  constructor(props) {
    super(props);
    this.onThemeSetupClick = this.onThemeSetupClick.bind(this);
  }

  componentDidMount() {
    const { formValues, themeSetting, selectedTheme, version } = this.props;
    this.toggleCarouselListener(String(formValues.layoutDisplay) === 'carousel');
    if (!_.isEmpty(themeSetting)) {
      this.setRecommendationPreviewSettings(formValues);
      this.setConfigPreview(formValues, themeSetting, themeSetting, this.props);
      this.initScript({ theme: selectedTheme }, version);
      this.updatePreview(formValues);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { formValues, previewSetting, themeSetting, version } = this.props;
    const {
      formValues: nextFormValues,
      previewSetting: nextPreviewSetting,
      themeSetting: nextThemeSetting,
      version: nextVersion,
      selectedTheme
    } = nextProps;
    const layoutDisplay = String(nextFormValues.layoutDisplay);

    if (String(formValues.layoutDisplay) !== layoutDisplay) {
      this.toggleCarouselListener(layoutDisplay === 'carousel');
    }
    if (layoutDisplay === 'bundle') {
      this.removeScripts();
    }
    if (
      layoutDisplay !== 'bundle' &&
      !_.isEmpty(nextPreviewSetting) &&
      !_.isEmpty(nextThemeSetting) &&
      (!_.isEqual(previewSetting, nextPreviewSetting) ||
        !_.isEqual(themeSetting, nextThemeSetting) ||
        !_.isEqual(version, nextVersion))
    ) {
      this.removeScripts();
      this.setRecommendationPreviewSettings(nextFormValues);
      this.setConfigPreview(formValues, nextPreviewSetting, nextThemeSetting, nextProps);
      this.initScript(selectedTheme, nextVersion);
      this.updatePreview(nextProps.formValues);
    }
    if (
      layoutDisplay !== 'bundle' &&
      !_.isEmpty(nextPreviewSetting) &&
      !_.isEmpty(nextThemeSetting) &&
      !_.isEqual(formValues, nextFormValues)
    ) {
      if (
        formValues.numberOfRecommendProduct !== nextFormValues.numberOfRecommendProduct ||
        String(formValues.layoutDisplay) !== layoutDisplay
      ) {
        this.removeScripts();
        this.setRecommendationPreviewSettings(formValues);
        this.setConfigPreview(formValues, nextPreviewSetting, nextThemeSetting, nextProps);
        this.initScript(selectedTheme, nextVersion);
      }
      this.setRecommendationPreviewSettings(nextFormValues);
      this.updatePreview(nextProps.formValues);
    }
  }

  componentWillUnmount() {
    this.removeScripts();
  }

  initScript = (selectedTheme: Object, version: string) => {
    const host =
      process.env.REACT_APP_BOOST_CDN_URL || 'https://boost-cdn-staging.bc-solutions.net';
    const themeName =
      selectedTheme.theme && selectedTheme.theme.boostThemeLib
        ? selectedTheme.theme.boostThemeLib
        : 'default';
    // Make sure react load done before load main
    this.reactScript = loadScriptWithCallback(REACT_SCRIPT, () => {
      this.reactDomScript = loadScriptWithCallback(REACT_DOM_SCRIPT, () => {
        this.vendorScript = loadScript(`${host}/theme/${themeName}/${version}/vendor.js`);
        this.mainScript = loadScript(`${host}/theme/${themeName}/${version}/main.js`);
      });
    });

    // If version >= 2.0.0 || version = alpha => not load file features => load main.css
    // else version < 2 or version = staging => load file features => support for old theme
    if (checkNewVersionBoostThemeLib(version)) {
      this.mainCSS = loadCSS(`${host}/theme/${themeName}/${version}/css/main.css`);
    } else
      this.filterScript = loadScript(`${host}/theme/${themeName}/${version}/recommendation.js`);
  };

  removeScripts = () => {
    // delete window.__BoostCustomization__;
    // delete window.Shopify;
    // delete window.boostSDAppConfig;
    // delete window.boostSDRecommendationConfig;
    // delete window.BoostComponentRegistry;
    removeScript(this.mainScript);
    removeScript(this.vendorScript);
    removeScript(this.reactScript);
    removeScript(this.reactDomScript);
    if (checkNewVersionBoostThemeLib(this.props.version)) removeScript(this.mainCSS);
    else removeScript(this.filterScript);
    window.boostSDAppLibWp = [];
  };

  getShopifyMetafieldSetting = formValues => {
    const themeSetting = _.cloneDeep(formValues);
    if (themeSetting?.cart_setting) {
      themeSetting.cart_setting.enableCart = false; // This preview is built in app-admin
    }

    const truthyFormValues = {};
    Object.keys(themeSetting).forEach(key => {
      if (themeSetting[key]) {
        return (truthyFormValues[key] = themeSetting[key]);
      }
    });

    const transformedData = convertToDataThemeSetting(truthyFormValues);
    const shopMetaFieldSetting = convertToShopifyThemeSetting(transformedData);
    return shopMetaFieldSetting;
  };

  setConfigPreview(formValues, previewSettings, themeSetting, nextProps = this.props) {
    const { pageType } = this.buildPageType(nextProps);
    const { tenantApp, tenantId, taeFeatures } = this.props;
    const widgetDesignShareableSettings = _.get(this.props, 'widgetDesignShareableSettings', {});

    previewSettings.generalSettings = {
      ...previewSettings.generalSettings,
      page: page[pageType],
      current_locale: 'en'
    };
    let { widgetName, ...widgetDesignSettings } = formValues;
    if (widgetDesignSettings?.templateType === 'default')
      widgetDesignSettings = { ...widgetDesignSettings, ...widgetDesignShareableSettings };
    const shopifyMetafieldSetting = this.getShopifyMetafieldSetting(themeSetting);
    const host = process.env.REACT_APP_ADMIN_V2_BASE_URL || 'https://staging.bc-solutions.net';
    window.boostSDAppConfig = {
      api: {
        filterUrl: `${host}/bc-sf-filter/filter`,
        searchUrl: `${host}/bc-sf-filter/search`,
        recommendUrl: `${host}/discovery/recommend`,
        suggestionUrl: `${host}/bc-sf-filter/search/suggest`,
        productsUrl: `${host}/bc-sf-filter/search/products`
      },
      shop: {
        name: tenantApp?.externalInfo?.name,
        url: `https://${tenantId}`,
        domain: tenantId,
        currency: tenantApp?.externalInfo?.currency,
        money_format: tenantApp?.externalInfo?.money_format || '&#36;{{amount}}',
        money_format_with_currency:
          tenantApp?.externalInfo?.money_with_currency_format || '&#36;{{amount}} USD'
      },
      primary_language: previewSettings.translation,
      ...previewSettings,
      themeSettings: shopifyMetafieldSetting,
      themeInfo: {
        taeFeatures: {
          ...taeFeatures
        }
      }
    };

    window.boostSDData = {
      product: undefined
    };
    window.__BoostCustomization__ = [
      registry => {
        registry.useComponentPlugin('RecommendationWidget', {
          apply() {
            return {
              props() {
                const { widgetId, pageType } = window.widgetInfo;
                const widgetOptions =
                  window.boostSDRecommendationConfig.widgets[page[pageType]][widgetId];
                const props = {
                  widgetName: widgetOptions.widgetName,
                  params: widgetOptions.params,
                  designSettings: widgetOptions.widgetDesignSettings,
                  page: page[pageType],
                  product: null
                };
                return props;
              },
              afterInit(element) {
                window.addEventListener('reload-recommendation-widget-setting', () => {
                  element.render();
                });
              }
            };
          }
        });

        registry.useComponentPlugin('SliderSlide', {
          apply() {
            return {
              afterRender(element) {
                const root = element.getRootElm();
                if (!root) return;

                const clickableElements = root.querySelectorAll('a, button');

                const preventClick = e => {
                  e.preventDefault();
                  e.stopPropagation();
                };

                root.addEventListener('click', preventClick, true);

                clickableElements.forEach(clickableElement => {
                  clickableElement.addEventListener('click', preventClick, true);
                });
              }
            };
          }
        });
      }
    ];
  }

  updatePreview(formValues) {
    const { widgetId, pageType } = this.buildPageType();
    let { widgetName } = formValues;

    window.boostSDRecommendationConfig.widgets[page[pageType]][widgetId].widgetName = widgetName;
    const reloadSettingsEvent = new CustomEvent('reload-recommendation-widget-setting');
    window.dispatchEvent(reloadSettingsEvent);
  }

  setRecommendationPreviewSettings(formValues) {
    const { widgetId, pageType } = this.buildPageType();
    const { selectedTheme, tenantApp } = this.props;
    const widgetDesignShareableSettings = _.get(this.props, 'widgetDesignShareableSettings', {});
    let { widgetName, ...widgetDesignSettings } = formValues;
    if (widgetDesignSettings?.templateType === 'default')
      widgetDesignSettings = { ...widgetDesignSettings, ...widgetDesignShareableSettings };
    window.boostSDRecommendationConfig = {
      widgets: {
        [page[pageType]]: {
          [widgetId]: {
            params: {
              shop: this.props.tenantId,
              recommendationType: 'most-viewed',
              limit: 30
            },
            widgetName: widgetName,
            widgetStatus: 'inactive',
            widgetDesignSettings: {
              ...widgetDesignSettings,
              layoutDisplay: String(widgetDesignSettings.layoutDisplay)
            }
          }
        }
      },
      defaultSettings: widgetDesignShareableSettings
    };
    window.Shopify = {
      currency: {
        active: tenantApp?.externalInfo?.currency,
        rate: '1'
      },
      shop: this.props.tenantId,
      locale: 'en',
      theme: {
        name: selectedTheme.label,
        id: selectedTheme.value
      },
      routes: {
        root: '/'
      }
    };
  }

  buildPageType(nextProps = this.props) {
    let { widgetId, pageType } = nextProps;
    if (pageType === 'cart-page') {
      widgetId = widgetId ? widgetId.replace('cartpage', 'homepage') : widgetId;
      pageType = 'home-page';
    }
    window.widgetInfo = { widgetId, pageType };
    return { widgetId, pageType };
  }

  toggleCarouselListener(addListener) {
    const container = document.querySelector('.visual-editor-preview');
    if (container && addListener) {
      container.addEventListener('click', this.handleCarouselClick);
    } else if (container) {
      container.removeEventListener('click', this.handleCarouselClick);
    }
  }

  handleCarouselClick(event) {
    event.preventDefault();
    const modal = document.querySelector('.boost-sd__modal');
    if (modal) modal.style.display = 'none';
    const body = document.querySelector('body');
    body.classList.remove('boost-sd__modal-open');
  }

  onLearnMoreClick() {
    const url = URL['CustomizingProductItemSection'];
    window.open(url, '_blank');
  }

  onThemeSetupClick() {
    const { host } = this.props;
    const shop = getShop();
    const url = `${routes.themeSetup.view()}?shop=${shop}&host=${host}`;
    window.open(url, '_blank');
  }

  render() {
    const { widgetId } = this.buildPageType();
    const { formValues, i18n, themesList, selectedThemeValue, handleChangeTheme } = this.props;
    const layoutDisplay = String(formValues.layoutDisplay);
    return (
      <div className="visual-editor-preview-wrapper">
        <div className="visual-editor-preview-header">
          <div className="theme-preview">
            <div className="theme-preview-label">
              <span>{i18n.translate('AdminHTML.Common.Action.preview')}</span>
            </div>
            <div className="select-theme-preview">
              <RenderSelect2
                name="themePreview"
                component={RenderSelect2}
                options={themesList}
                placeholder={i18n.translate(
                  'AdminHTML.ThemeSetup.ThemeSetup.step2_Theme_Placeholder'
                )}
                isDisabled={_.isEmpty(themesList)}
                onChange={e => handleChangeTheme(e)}
                value={selectedThemeValue}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="visual-editor-preview">
            {layoutDisplay !== 'bundle' ? (
              <div className="recommendation-carousel">
                <div
                  className="recommendation-carousel-body"
                  id={`boost-sd-widget-${widgetId}`}></div>
              </div>
            ) : (
              <React.Fragment>
                <BundlePreviewVisualEditor />
              </React.Fragment>
            )}
            <div className="recommendation-note">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.Recommendation.VisualEditor.preview_note1')}
              </TextStyle>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.Recommendation.VisualEditor.preview_note2')}
              </TextStyle>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({});

const mapStateToProps = (state, props) => {
  const widgetDesignShareableSettings = _.get(
    state.recommendation,
    'widgetDesignShareableSettings',
    {}
  );
  const widgetId = _.get(props.widget, 'id', null);
  const pageType = _.get(props.widget, 'pageType', null);
  const formValues = getFormValues('VisualEditorForm')(state);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const tenantId = _.get(tenantApp, 'tenantId', null);

  // get current selected theme infos
  const themes: Array<any> = _.get(state.themeSetup, 'themes', []);
  const themeInfo = themes.find(theme => theme.id === props.selectedTheme.value);

  // get current setting version of theme lib
  const hasFELibPreview = _.get(tenantApp, 'settings.FELibPreview.settings.enable', false);
  const settingVersion = _.get(tenantApp, 'settings.FELibPreview.settings.version', '');
  let version =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? _.get(themeInfo, 'boostThemeLibVersion', 'latest')
      : 'staging';

  // Use theme lib version setting if have
  if (hasFELibPreview && !_.isEmpty(settingVersion)) {
    version = settingVersion;
  }
  const taeFeatures = buildRecommendationTaeFeatures(themeInfo, widgetId, pageType);
  return {
    tenantId,
    pageType,
    formValues,
    widgetId,
    widgetDesignShareableSettings,
    tenantApp,
    previewSetting: _.get(state, 'themeSetting.previewSetting', {}),
    themeSetting: _.get(state, 'themeSetting.themeSetting', {}),
    taeFeatures,
    getThemesLoading: _.get(state.themeSetup, 'getThemesLoading', false),
    version,
    host: _.get(state.oauth, 'host', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PreviewVisualEditor));
